import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { loadUser } from "../slices/userSlice";
import { updateProfile, resetUpdate } from "../slices/profileSlice";
import { usePermissions } from "../utils/permissions";
import PermissionGate from "../componets/PermissionGate";
import BottomNavbar from "../componets/BottomNavbar";

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { loading, isUpdated } = useSelector((state) => state.profile);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const { hasPermission } = usePermissions();

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    } else if (!hasPermission("profile", "read")) {
      navigate("/my-business");
    }
  }, [isAuthenticated, navigate, hasPermission]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
      });
    }
  }, [user]);

  useEffect(() => {
    if (isUpdated) {
      dispatch(loadUser());
      dispatch(resetUpdate());
    }
  }, [dispatch, isUpdated]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.trim().length < 4) {
      errors.name = "Name must be at least 4 characters";
    } else if (formData.name.trim().length > 30) {
      errors.name = "Name cannot exceed 30 characters";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(updateProfile(formData)).unwrap();
    } catch (err) {
      // Error is handled by the slice with toast
    }
  };

  return (
    <div style={styles.container}>
      <div style={{
        ...styles.content,
        padding: isMobile ? "16px" : "24px",
        paddingBottom: isMobile ? "100px" : "100px",
      }}>
        <div style={{
          ...styles.card,
          padding: isMobile ? "20px 16px" : "30px",
        }}>
          <h2 style={{
            ...styles.title,
            fontSize: isMobile ? "24px" : "28px",
          }}>Update Profile</h2>

          <form onSubmit={handleSubmit} style={{
            ...styles.form,
            gap: isMobile ? "16px" : "20px",
          }}>
            <div style={styles.formGroup}>
              <label htmlFor="name" style={styles.label}>
                Full Name <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  borderColor: validationErrors.name ? "#dc3545" : "#e9edef",
                  width: "100%",
                }}
                placeholder="Enter full name"
                disabled={loading}
              />
              {validationErrors.name && (
                <span style={styles.errorText}>{validationErrors.name}</span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>
                Email Address <span style={styles.required}>*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  borderColor: validationErrors.email ? "#dc3545" : "#e9edef",
                  width: "100%",
                }}
                placeholder="Enter email address"
                disabled={loading}
              />
              {validationErrors.email && (
                <span style={styles.errorText}>{validationErrors.email}</span>
              )}
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                padding: isMobile ? "12px 20px" : "12px 24px",
              }}
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Profile"}
            </button>
          </form>

          <PermissionGate resource="profile" action="update">
            <div style={{
              ...styles.actions,
              marginTop: isMobile ? "16px" : "20px",
            }}>
              <Link to="/change-password" style={styles.linkButton}>
                Change Password
              </Link>
            </div>
          </PermissionGate>
        </div>
      </div>
      <BottomNavbar />
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
    paddingBottom: "80px",
  },
  content: {
    maxWidth: "600px",
    margin: "0 auto",
    padding: "24px",
    minHeight: "calc(100vh - 80px)",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "30px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e9edef",
  },
  title: {
    fontSize: "28px",
    fontWeight: "500",
    color: "#111b21",
    marginBottom: "24px",
    letterSpacing: "-0.02em",
  },
  form: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  required: {
    color: "#dc3545",
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: "400",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    color: "#111b21",
    letterSpacing: "-0.01em",
    boxSizing: "border-box",
  },
  inputError: {
    borderColor: "#dc3545",
  },
  errorText: {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
    fontWeight: "400",
  },
  button: {
    width: "100%",
    padding: "12px 24px",
    fontSize: "15px",
    fontWeight: "400",
    color: "#ffffff",
    backgroundColor: "#dc3545",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    marginTop: "10px",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    letterSpacing: "-0.01em",
  },
  actions: {
    marginTop: "20px",
    textAlign: "center",
    paddingTop: "20px",
    borderTop: "1px solid #e9edef",
  },
  linkButton: {
    color: "#00a884",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "400",
    letterSpacing: "-0.01em",
  },
};

export default Profile;

